import { normalizePlatformProxyHost, resolveRealtimeConnectionLimit } from "./server-platform-app-proxy.js";
import {
  buildServerPlatformNodeRouteRecord,
  createServerNodeProxyLoopbackRequest,
  resolvePlatformControlPlaneAppNameFromPath,
  resolvePlatformControlPlaneAppNameFromProxyHeaders,
} from "./server-platform-node-routing.js";
import { getNodeRouteMetricsSnapshot } from "./server-node-routing-metrics.js";
import * as wsGateway from "./ws-gateway.js";

type LoggerLike = Pick<Console, "log" | "warn" | "error">;
type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;

export function registerPlatformNodeOpsForRuntime(params: {
  nodeOpsRoutes: any;
  app: any;
  db: any;
  appsTable: any;
  platformNodes: any;
  eqOp: (left: unknown, right: unknown) => unknown;
  rawSql: RawSql;
  platformDomain: string;
  platformNodeId: string | null;
  getCapacity: () => unknown;
  getRuntimeMetrics: () => unknown;
  getLivePort: (appName: string) => number | undefined;
  setIntervalImpl: typeof setInterval;
  clearIntervalImpl: typeof clearInterval;
  logger: LoggerLike;
}): void {
  if (!params.nodeOpsRoutes?.registerPlatformNodeOpsRoutes) return;

  const nodeOpsParams = {
    app: params.app,
    currentNodeId: params.platformNodeId,
    envName: process.env.GENCOW_ENV || (params.platformDomain.includes("dev") ? "dev" : "prod"),
    currentInternalOrigin: process.env.GENCOW_NODE_INTERNAL_ORIGIN || null,
    currentActivePort: Number(process.env.PORT || 4100),
    rawSql: params.rawSql,
    getCapacity: params.getCapacity,
    getRuntimeMetrics: params.getRuntimeMetrics,
    getRouteMetrics: getNodeRouteMetricsSnapshot,
    getBuildHash: () => process.env.GIT_SHA || process.env.RENDER_GIT_COMMIT || null,
    resolveRouteDiagnostic: async (appName: string) => {
      const rows = await params.db
        .select({ name: params.appsTable.name, nodeId: params.appsTable.nodeId, port: params.appsTable.port })
        .from(params.appsTable)
        .where(params.eqOp(params.appsTable.name, appName))
        .limit(1);
      const appRow = rows[0];
      if (!appRow) {
        return params.nodeOpsRoutes.buildPlatformNodeRouteDiagnostic({
          appName,
          currentNodeId: params.platformNodeId,
          app: null,
        });
      }
      const currentNodeId = params.platformNodeId || appRow.nodeId || "node-1";
      let homeNode: ReturnType<typeof buildServerPlatformNodeRouteRecord> = null;
      if (params.platformNodes && appRow.nodeId && appRow.nodeId !== currentNodeId) {
        const nodeRows = await params.db
          .select({
            id: params.platformNodes.id,
            status: params.platformNodes.status,
            internalOrigin: params.platformNodes.internalOrigin,
            lastHeartbeatAt: params.platformNodes.lastHeartbeatAt,
          })
          .from(params.platformNodes)
          .where(params.eqOp(params.platformNodes.id, appRow.nodeId))
          .limit(1);
        homeNode = nodeRows[0] ? buildServerPlatformNodeRouteRecord(nodeRows[0]) : null;
      }
      return params.nodeOpsRoutes.buildPlatformNodeRouteDiagnostic({
        appName,
        currentNodeId,
        app: appRow,
        homeNode,
        localPort: params.getLivePort(appName) ?? null,
      });
    },
    logger: params.logger,
  };
  params.nodeOpsRoutes.registerPlatformNodeOpsRoutes(nodeOpsParams);
  params.nodeOpsRoutes.startPlatformNodeHeartbeatLoop?.({
    ...nodeOpsParams,
    setIntervalImpl: params.setIntervalImpl,
    clearIntervalImpl: params.clearIntervalImpl,
  });
}

export function registerNodeProxyInternalRoutesForRuntime(params: {
  nodeProxyEnabled: boolean;
  nodeProxyInternalRoutes: any;
  app: any;
  db: any;
  appsTable: any;
  eqOp: (left: unknown, right: unknown) => unknown;
  andOp: (...conditions: any[]) => unknown;
  rawSql: RawSql;
  platformDomain: string;
  subdomainRegex: RegExp;
  platformNodeId: string | null;
  nodeProxyKeyId: string | null;
  nodeProxySecret: string | null;
  generateInternalToken: (appId: number, appName: string) => string;
  getLivePort: (appName: string) => number | undefined;
  logger: LoggerLike;
}): void {
  if (
    !params.nodeProxyEnabled ||
    !params.nodeProxyInternalRoutes ||
    !params.platformNodeId ||
    !params.nodeProxyKeyId ||
    !params.nodeProxySecret
  ) {
    return;
  }

  const resolveAppName = async ({
    headers,
    originalHost,
    originalPath,
  }: {
    headers: Headers;
    originalHost: string;
    originalPath: string;
  }) => {
    const host = normalizePlatformProxyHost(originalHost);
    const platformHost = normalizePlatformProxyHost(params.platformDomain);
    if (host === platformHost || host === `www.${platformHost}`) {
      const controlPlaneAppName = resolvePlatformControlPlaneAppNameFromPath(originalPath);
      if (controlPlaneAppName) return controlPlaneAppName;
      const rpcControlPlaneAppName = resolvePlatformControlPlaneAppNameFromProxyHeaders(originalPath, headers);
      if (rpcControlPlaneAppName) return rpcControlPlaneAppName;
    }

    const match = host.match(params.subdomainRegex);
    if (match?.[1]) return match[1];
    const rows = await params.db
      .select({ name: params.appsTable.name })
      .from(params.appsTable)
      .where(params.andOp(params.eqOp(params.appsTable.customDomain, host), params.eqOp(params.appsTable.customDomainStatus, "active")))
      .limit(1);
    return rows[0]?.name ?? null;
  };

  params.nodeProxyInternalRoutes.registerNodeProxyHttpRoute({
    app: params.app,
    currentNodeId: params.platformNodeId,
    secretsByKeyId: { [params.nodeProxyKeyId]: params.nodeProxySecret },
    resolveAppName,
    proxyHomeAppRequest: async ({ c, appName, originalHost, originalPath, body }: any) => {
      const rows = await params.db
        .select({ nodeId: params.appsTable.nodeId })
        .from(params.appsTable)
        .where(params.eqOp(params.appsTable.name, appName))
        .limit(1);
      const appNodeId = rows[0]?.nodeId?.trim() || params.platformNodeId;
      if (appNodeId && appNodeId !== params.platformNodeId) {
        return Response.json({ error: "Node assignment changed", code: "NODE_ASSIGNMENT_CHANGED" }, { status: 409 });
      }
      return params.app.fetch(createServerNodeProxyLoopbackRequest({ c, originalHost, originalPath, body }), c.env);
    },
    logger: params.logger,
  });

  params.nodeProxyInternalRoutes.registerNodeProxyWsRoute({
    app: params.app,
    currentNodeId: params.platformNodeId,
    secretsByKeyId: { [params.nodeProxyKeyId]: params.nodeProxySecret },
    resolveAppName,
    upgradeHomeAppWsRequest: async ({ c, appName, originalPath }: any) => {
      const rows = await params.db
        .select({ id: params.appsTable.id, name: params.appsTable.name, port: params.appsTable.port, nodeId: params.appsTable.nodeId })
        .from(params.appsTable)
        .where(params.eqOp(params.appsTable.name, appName))
        .limit(1);
      const appRow = rows[0];
      if (!appRow) {
        return Response.json({ error: "App not found", code: "NODE_PROXY_APP_NOT_FOUND" }, { status: 404 });
      }
      const appNodeId = appRow.nodeId?.trim() || params.platformNodeId;
      if (appNodeId && appNodeId !== params.platformNodeId) {
        return Response.json({ error: "Node assignment changed", code: "NODE_ASSIGNMENT_CHANGED" }, { status: 409 });
      }
      const appInternalToken = params.generateInternalToken(appRow.id, appRow.name);
      const originalUrl = new URL(`http://placeholder${originalPath}`);
      const ticketAuth = wsGateway.consumeGatewayRealtimeTicket({
        ticket: originalUrl.searchParams.get("ticket"),
        appName: appRow.name,
        secret: appInternalToken,
      });
      if (!ticketAuth.ok) {
        return new Response("Invalid realtime ticket", { status: ticketAuth.status });
      }
      const connectionLimit = await resolveRealtimeConnectionLimit({
        appName: appRow.name,
        rawSql: params.rawSql,
        logger: params.logger,
      });
      const upgraded = c.env?.server?.upgrade?.(c.req.raw, {
        data: {
          gatewayAppName: appRow.name,
          appName: appRow.name,
          appPort: params.getLivePort(appRow.name) || appRow.port,
          pathname: originalUrl.pathname,
          connectionLimit,
          appInternalToken,
          authMode: ticketAuth.authMode,
          userId: ticketAuth.authMode === "authenticated" ? ticketAuth.userId : undefined,
          sessionId: ticketAuth.authMode === "authenticated" ? ticketAuth.sessionId : null,
          authExpiresAt: ticketAuth.authMode === "authenticated" ? ticketAuth.expiresAt : undefined,
        },
      });
      return upgraded
        ? new Response(null, { status: 101 })
        : Response.json({ error: "WebSocket upgrade failed", code: "NODE_PROXY_WS_UPGRADE_FAILED" }, { status: 500 });
    },
    logger: params.logger,
  });
}
